import * as PIXI from "pixi.js";
import { Assets, BitmapFontInstallOptions, BitmapFontData } from "pixi.js";

// This only exists because Parcel and Pixi don't play well together
class BundlingUtils {
    private static _instance: BundlingUtils;

    public static getInstance() {
        if (!this._instance) {
            this._instance = new BundlingUtils();
        }

        return this._instance;
    }
    /*
     * atlasPath - the json path
     */
    public async createSpritesheet(
        atlasJsonPath: string,
        atlasImgPath: string,
    ): Promise<PIXI.Spritesheet> {
        // Manually create the atlas
        const atlasResponse = await fetch(atlasJsonPath);
        const atlasData = await atlasResponse.json();
        // console.log("before");
        // console.log(atlasData);
        atlasData.meta.image = atlasImgPath;
        // console.log("after");
        // console.log(atlasData);

        // Load the PNG texture and create spritesheet
        const baseTexture = await Assets.load(atlasImgPath);
        const spritesheet = new PIXI.Spritesheet(baseTexture, atlasData);
        await spritesheet.parse();

        return spritesheet;
    }

    public async createSpineAtlas(
        atlasPath: string,
        atlasImgPath: string,
    ): Promise<any> {
        const atlasResponse = await fetch(atlasPath);
        const atlasText = await atlasResponse.text();

        const lines = atlasText.split("\n");
        const imageName = atlasImgPath.split("/").pop();
        lines[0] = imageName || lines[0];

        const cleanedLines = lines.filter((line) => !line.trim().startsWith("#"));
        const fixedAtlasText = cleanedLines.join("\n");

        const pixiTexture = await Assets.load(atlasImgPath);

        // Import the required classes
        const { TextureAtlas } = await import("@esotericsoftware/spine-core");
        const { SpineTexture } = await import("@pixi/spine-pixi");

        // Create the TextureAtlas
        const atlas = new TextureAtlas(fixedAtlasText);
        // Create the proper SpineTexture from the PIXI texture source
        const spineTexture = SpineTexture.from(pixiTexture.source);

        // Set the SpineTexture on all pages using the setTexture method
        atlas.pages.forEach((page) => {
            page.setTexture(spineTexture);
        });

        return atlas;
    }

    // Preferable to use .fnt, xml currently not supported
    public async createBitmapFont(
        fontPath: string,
        fontImgPath: string,
        fontName: string
    ): Promise<void> {
        const fontText = await fetch(fontPath).then(res => res.text());
        const fontTexture = await Assets.load(fontImgPath);

        const fontData = this._parseBMFontText(fontText, fontImgPath);

        // Debug: Log what characters we have
        // console.log('Available characters:', Object.keys(fontData.chars));
        // console.log('Font data:', fontData);

        const bitmapFont = new PIXI.BitmapFont(
            {
                data: fontData,
                textures: [fontTexture],
            },
            fontName
        );

        // console.log(bitmapFont);

        // Register it in PIXI's font cache
        PIXI.Assets.cache.set(fontName, bitmapFont);
    }

    private _parseBMFontText(fontText: string, imagePath: string): BitmapFontData {
        // Retrieve data item
        const items = fontText.match(/^[a-z]+\s+.+$/gm);
        const rawData: Record<string, any[]> = {
            info: [],
            common: [],
            page: [],
            char: [],
            chars: [],
            kerning: [],
            kernings: [],
            distanceField: [],
        };

        if (!items) {
            throw new Error('Invalid font data');
        }

        for (let i = 0; i < items.length; i++) {
            // Extract item name
            const nameMatch = items[i].match(/^[a-z]+/gm);
            if (!nameMatch) continue;
            const name = nameMatch[0];

            // Extract item attribute list as string ex.: "width=10"
            const attributeList = items[i].match(/[a-zA-Z]+=([^\s"']+|"([^"]*)")/gm);
            if (!attributeList) continue;

            // Convert attribute list into an object
            const itemData: any = {};

            for (let j = 0; j < attributeList.length; j++) {
                // Split key-value pairs
                const split = attributeList[j].split('=');
                const key = split[0];

                // Remove eventual quotes from value
                const strValue = split[1].replace(/"/gm, '');

                // Try to convert value into float
                const floatValue = parseFloat(strValue);

                // Use string value case float value is NaN
                const value = isNaN(floatValue) ? strValue : floatValue;

                itemData[key] = value;
            }

            // Push current item to the resulting data
            if (rawData[name]) {
                rawData[name].push(itemData);
            }
        }

        const font: BitmapFontData = {
            chars: {},
            pages: [],
            lineHeight: 0,
            fontSize: 0,
            fontFamily: '',
            distanceField: undefined,
            baseLineOffset: 0,
        };

        const info = rawData.info?.[0];
        const common = rawData.common?.[0];
        const distanceField = rawData.distanceField?.[0];

        if (distanceField) {
            font.distanceField = {
                range: parseInt(distanceField.distanceRange, 10),
                type: distanceField.fieldType
            };
        }

        if (info) {
            font.fontSize = parseInt(info.size, 10);
            font.fontFamily = info.face;
        }

        if (common) {
            font.lineHeight = parseInt(common.lineHeight, 10);
            font.baseLineOffset = font.lineHeight - parseInt(common.base, 10);
        }

        const pages = rawData.page || [];
        for (let i = 0; i < pages.length; i++) {
            font.pages.push({
                id: parseInt(pages[i].id, 10) || 0,
                file: pages[i].file,
            });
        }

        const map: Record<string, string> = {};
        const chars = rawData.char || [];

        for (let i = 0; i < chars.length; i++) {
            const charNode = chars[i];
            const id = parseInt(charNode.id, 10);

            let letter = charNode.letter ?? charNode.char ?? String.fromCharCode(id);

            if (letter === 'space') letter = ' ';

            map[id] = letter;

            font.chars[letter] = {
                id,
                letter, // Added missing letter property
                page: parseInt(charNode.page, 10) || 0,
                x: parseInt(charNode.x, 10),
                y: parseInt(charNode.y, 10),
                width: parseInt(charNode.width, 10),
                height: parseInt(charNode.height, 10),
                xOffset: parseInt(charNode.xoffset, 10),
                yOffset: parseInt(charNode.yoffset, 10),
                xAdvance: parseInt(charNode.xadvance, 10),
                kerning: {},
            };
        }

        // console.log('Final character map:', font.chars); // Debug log

        const kernings = rawData.kerning || [];
        for (let i = 0; i < kernings.length; i++) {
            const first = parseInt(kernings[i].first, 10);
            const second = parseInt(kernings[i].second, 10);
            const amount = parseInt(kernings[i].amount, 10);

            if (font.chars[map[second]]) {
                font.chars[map[second]].kerning[map[first]] = amount;
            }
        }

        return font;
    }

    // public async createBitmapFont(
    //     fontPath: string,
    //     fontImgPath: string,
    //     fontName: string,
    // ): Promise<void> {
    //     // Fetch the font data
    //     const response = await fetch(fontPath);
    //     const fontText = await response.text();

    //     // Load the font texture
    //     const fontTexture = await Assets.load(fontImgPath);

    //     let fontData: PIXI.BitmapFontData;

    //     // Check if it's XML or text format
    //     if (fontText.trim().startsWith("<")) {
    //         // XML format - use existing parser
    //         const parser = new DOMParser();
    //         const xmlDoc = parser.parseFromString(fontText, "text/xml");
    //         fontData = this._parseFontXML(xmlDoc, fontImgPath);
    //     } else {
    //         // Text format - parse as BMFont text
    //         fontData = this._parseBMFontText(fontText, fontImgPath);
    //     }

    //     // Create the bitmap font and register it globally
    //     const bitmapFont = new PIXI.BitmapFont(
    //         {
    //             data: fontData,
    //             textures: [fontTexture],
    //         },
    //         fontPath,
    //     );

    //     // Register it in PIXI's font cache so you can use it by name
    //     PIXI.Assets.cache.set(fontName, bitmapFont);
    // }

    // private _parseBMFontText(
    //     fontText: string,
    //     imagePath: string,
    // ): PIXI.BitmapFontData {
    //     const lines = fontText.split("\n");

    //     let fontFamily = "Unknown";
    //     let fontSize = 12;
    //     let lineHeight = 12;
    //     let baseLineOffset = 0;
    //     const charData: Record<string, any> = {};
    //     const pages: any[] = [];

    //     for (const line of lines) {
    //         const trimmedLine = line.trim();

    //         if (trimmedLine.startsWith("info ")) {
    //             // Parse info line: info face="winFont" size=72 ...
    //             const faceMatch = trimmedLine.match(/face="([^"]+)"/);
    //             const sizeMatch = trimmedLine.match(/size=(\d+)/);

    //             if (faceMatch) fontFamily = faceMatch[1];
    //             if (sizeMatch) fontSize = parseInt(sizeMatch[1]);
    //         } else if (trimmedLine.startsWith("common ")) {
    //             // Parse common line: common lineHeight=76 base=59 ...
    //             const lineHeightMatch = trimmedLine.match(/lineHeight=(\d+)/);
    //             const baseMatch = trimmedLine.match(/base=(\d+)/);

    //             if (lineHeightMatch) lineHeight = parseInt(lineHeightMatch[1]);
    //             if (baseMatch) baseLineOffset = lineHeight - parseInt(baseMatch[1]); // Calculate offset like PIXI does
    //         } else if (trimmedLine.startsWith("page ")) {
    //             // Parse page line: page id=0 file="winFont.png"
    //             const idMatch = trimmedLine.match(/id=(\d+)/);
    //             const fileMatch = trimmedLine.match(/file="([^"]+)"/);

    //             if (idMatch && fileMatch) {
    //                 pages.push({
    //                     id: parseInt(idMatch[1]),
    //                     file: fileMatch[1],
    //                 });
    //             }
    //         } else if (trimmedLine.startsWith("char ")) {
    //             // Parse char line with EXACT property names PIXI expects
    //             const matches = {
    //                 id: trimmedLine.match(/id=(\d+)/)?.[1],
    //                 x: trimmedLine.match(/x=(\d+)/)?.[1],
    //                 y: trimmedLine.match(/y=(\d+)/)?.[1],
    //                 width: trimmedLine.match(/width=(\d+)/)?.[1],
    //                 height: trimmedLine.match(/height=(\d+)/)?.[1],
    //                 xoffset: trimmedLine.match(/xoffset=(-?\d+)/)?.[1],
    //                 yoffset: trimmedLine.match(/yoffset=(-?\d+)/)?.[1],
    //                 xadvance: trimmedLine.match(/xadvance=(\d+)/)?.[1],
    //                 page: trimmedLine.match(/page=(\d+)/)?.[1],
    //             };

    //             if (matches.id) {
    //                 const id = parseInt(matches.id);
    //                 const letter = String.fromCharCode(id);

    //                 // Use EXACT property names that PIXI expects
    //                 charData[letter] = {
    //                     id,
    //                     page: parseInt(matches.page || "0"),
    //                     x: parseInt(matches.x || "0"),
    //                     y: parseInt(matches.y || "0"),
    //                     width: parseInt(matches.width || "0"),
    //                     height: parseInt(matches.height || "0"),
    //                     xOffset: parseInt(matches.xoffset || "0"), // Note: xOffset (camelCase)
    //                     yOffset: parseInt(matches.yoffset || "0"), // Note: yOffset (camelCase)
    //                     xAdvance: parseInt(matches.xadvance || "0"), // Note: xAdvance (camelCase)
    //                     kerning: {}, // Initialize empty kerning object
    //                 };
    //             }
    //         } else if (trimmedLine.startsWith("kerning ")) {
    //             // Parse kerning line: kerning first=65 second=84 amount=-5
    //             const firstMatch = trimmedLine.match(/first=(\d+)/);
    //             const secondMatch = trimmedLine.match(/second=(\d+)/);
    //             const amountMatch = trimmedLine.match(/amount=(-?\d+)/);

    //             if (firstMatch && secondMatch && amountMatch) {
    //                 const firstChar = String.fromCharCode(parseInt(firstMatch[1]));
    //                 const secondChar = String.fromCharCode(parseInt(secondMatch[1]));
    //                 const amount = parseInt(amountMatch[1]);

    //                 // Add kerning data - PIXI expects kerning[second].kerning[first] = amount
    //                 if (charData[secondChar]) {
    //                     charData[secondChar].kerning[firstChar] = amount;
    //                 }
    //             }
    //         }
    //     }

    //     // Return the exact structure PIXI expects for BitmapFontData
    //     return {
    //         fontFamily,
    //         fontSize,
    //         lineHeight,
    //         baseLineOffset,
    //         chars: charData,
    //         pages:
    //             pages.length > 0
    //                 ? pages
    //                 : [
    //                     {
    //                         id: 0,
    //                         file: imagePath.split("/").pop() || "font.png",
    //                     },
    //                 ],
    //         distanceField: { type: "none", range: 0 }, // Add this property that PIXI expects
    //     };
    // }

    // private _parseFontXML(
    //     xmlDoc: Document,
    //     imagePath: string,
    // ): PIXI.BitmapFontData {
    //     const info = xmlDoc.querySelector("info");
    //     const common = xmlDoc.querySelector("common");
    //     const chars = xmlDoc.querySelectorAll("char");
    //     const kernings = xmlDoc.querySelectorAll("kerning"); // Add kerning support

    //     // Parse character data
    //     const charData: Record<string, any> = {};
    //     const charMap: Record<string, string> = {}; // Map IDs to characters

    //     chars.forEach((char) => {
    //         const id = parseInt(char.getAttribute("id") || "0");
    //         const letter = String.fromCharCode(id);
    //         charMap[id] = letter; // Store mapping for kerning

    //         charData[letter] = {
    //             id,
    //             page: parseInt(char.getAttribute("page") || "0"),
    //             x: parseInt(char.getAttribute("x") || "0"),
    //             y: parseInt(char.getAttribute("y") || "0"),
    //             width: parseInt(char.getAttribute("width") || "0"),
    //             height: parseInt(char.getAttribute("height") || "0"),
    //             xOffset: parseInt(char.getAttribute("xoffset") || "0"), // Note: camelCase
    //             yOffset: parseInt(char.getAttribute("yoffset") || "0"), // Note: camelCase
    //             xAdvance: parseInt(char.getAttribute("xadvance") || "0"), // Note: camelCase
    //             kerning: {}, // Initialize empty kerning object
    //         };
    //     });

    //     // Parse kerning data
    //     kernings.forEach((kerning) => {
    //         const first = parseInt(kerning.getAttribute("first") || "0");
    //         const second = parseInt(kerning.getAttribute("second") || "0");
    //         const amount = parseInt(kerning.getAttribute("amount") || "0");

    //         const firstChar = charMap[first];
    //         const secondChar = charMap[second];

    //         if (firstChar && secondChar && charData[secondChar]) {
    //             charData[secondChar].kerning[firstChar] = amount;
    //         }
    //     });

    //     return {
    //         fontFamily: info?.getAttribute("face") || "Unknown",
    //         fontSize: parseInt(info?.getAttribute("size") || "12"),
    //         lineHeight: parseInt(common?.getAttribute("lineHeight") || "12"),
    //         baseLineOffset:
    //             parseInt(common?.getAttribute("lineHeight") || "12") -
    //             parseInt(common?.getAttribute("base") || "0"), // Calculate like PIXI does
    //         chars: charData,
    //         pages: [
    //             {
    //                 id: 0,
    //                 file: imagePath.split("/").pop() || "font.png",
    //             },
    //         ],
    //         distanceField: { type: "none", range: 0 }, // Add this property that PIXI expects
    //     };
    // }
}

export const bundlingUtils = BundlingUtils.getInstance();
